'use client';
import React, { createContext, useContext, useMemo } from 'react';

// Wraps up the business locations as provided by the API into a context provider so that it can be used by child components
import IBusinessLocation from '@/app/(auth)/signup/types/IBusinessLocation';
import { BusinessGraphNode, IBusinessGraphNode } from '@/utils/BusinessGraph';
import { logrocketService } from '@/services/logrocket';

interface IBusinessLocationContext {
  locations: IBusinessLocation[];
  graphRoots: IBusinessGraphNode[];
  locationsById: Record<string, IBusinessLocation>;
}

const BusinessLocationsContext = createContext<IBusinessLocationContext>({} as IBusinessLocationContext);

export function BusinessLocationsProvider({
  children,
  locations,
}: {
  children: React.ReactNode;
  locations: IBusinessLocation[];
}) {
  const context = useMemo(() => {
    const locationsById = locations.reduce(
      (acc, location) => {
        acc[location.Id] = location;
        return acc;
      },
      {} as Record<string, IBusinessLocation>,
    );

    const graphRoots = BusinessGraphNode.buildBusinessGraphs(locations);

    return {
      locations,
      graphRoots,
      locationsById,
    };
  }, [locations]);

  return <BusinessLocationsContext.Provider value={context}>{children}</BusinessLocationsContext.Provider>;
}

export function useBusinessLocationsContext() {
  const context = useContext(BusinessLocationsContext);

  if (!context || context.locations === undefined) {
    const error = new Error('useBusinessLocations must be used within a BusinessLocationsProvider');
    logrocketService.logException(error);
    throw error;
  }

  return context;
}

export function useBusinessLocationForId(id: number) {
  const { locationsById } = useBusinessLocationsContext();
  return locationsById[id];
}

export function useBusinessLocationGraphs() {
  const { graphRoots } = useBusinessLocationsContext();
  return graphRoots;
}
